import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckBox, Column, EntryForm, Input, TextArea } from "../components/EntryForm";
import Page from "../components/Page"
import { useUserProvider } from "../providers/UserProvider";
import { useFormik } from "formik";
import * as Yup from "yup";
import { validations } from "../lib/form";
import { useModalProvider } from "../providers/ModalProvider";
import { useHistory } from "react-router-dom";
import responseProcessor from "../lib/responseProcessor";
import DataSelector from "../components/DataSelector";
import { AddAppResource } from "../services/api/app";
import { useLoaderProvider } from "../providers/LoaderProvider";
import { Radio } from "antd";
import DateSelector from "../components/DateSelectorWithTimezone";
import MediaUploader from "../components/MediaUploader";

import contentConfig from "../lib/content_config/config";
import SubTable from "../components/SubTable";
import { GetContent, UpdateContent } from "../services/api/content";
import VersionizerWrapper from "../components/Versionizer";
import moment from "moment";

const FlexContentDetail = ({
  newFlexContent,
  match
}) => {
  const { user } = useUserProvider();

  const defaultValues = {
    title: "",
    description: "",
    cta_type: "URL CTA",
    action_bar_text: "",
    action_bar_url: "",
    enterprise_app: null,
    cta: null,
    image_url: "",
    release_date: moment.tz(new Date(), user?.data.timezone).format('YYYY-MM-DD'),
    sort_order: "",
    tags: [],
    categories: [],
    featured: false,
    active: false,
    sections: []
  }

  const [isLoading, setIsLoading] = useState(false);
  const [currentDetail, setCurrentDetail] = useState(defaultValues);
  const [metadata, setMetadata] = useState({});
  const platform_version = user?.app_data?.about?.platform_version;

  const { showModal } = useModalProvider();
  const { doSetShowLoadingModal } = useLoaderProvider()
  const history = useHistory()

  const uuid = newFlexContent ? user.currentApp() : match.params.uuid;

  const currentAction = newFlexContent ? "New" : "Edit";
  const currentUrl = newFlexContent ? "/flex-content/new" : `/flex-content/${uuid}`;

  const subTables = contentConfig("flexes").subTables;

  const showBusiness = useMemo(() => (user?.app_data?.setup?.enterprise && user?.app_data?.setup?.enterprise_launch_business), [user]);

  let links = [
    { name: "Home", url: "/" },
    { name: "Flex Content List", url: "/flex-content" },
    { name: `${currentAction} Flex Content`, url: currentUrl },
  ];

  const handleSuccess = () => {
    setIsLoading(false);
    doSetShowLoadingModal(false)
    history.push("/flex-content")
  };
  
  const handleError = () => {
    setIsLoading(false);
    doSetShowLoadingModal(false)
  };

  const responseProcessorProxy = (response, additionalOnSuccess) => {
    responseProcessor({
      response,
      showModal,
      onSuccess: () => {
        setIsLoading(false)
        handleSuccess();
        if (additionalOnSuccess) {
          additionalOnSuccess();
        }
      },
      onError: handleError,
    });
  };

  const formik = useFormik({
    initialValues: currentDetail,
    enableReinitialize: true,
    validationSchema: () =>
      Yup.lazy((values) => {
        let schema = {
          title: validations.str_req_60,
          description: validations.long_text_required,
          cta_type: Yup.string().required("Required"),
          action_bar_text: validations.str_notreq_50,
          action_bar_url: validations.str_255,
          enterprise_app: Yup.object({
            id: Yup.string().nullable(),
            name: Yup.string().nullable(),
          }).nullable(),
          cta: Yup.object({
            id: Yup.string().nullable(),
            name: Yup.string().nullable(),
          }).nullable(),
          release_date: Yup.date(),
          sort_order: validations.sort_order,
          tags: Yup.array(),
          active: validations.bool,
          featured: validations.bool,
          image_url: validations.long_text,
          sections: Yup.array(Yup.object({
            sort_order: validations.sort_order,
            sub_description: validations.long_text_required,
            headline: validations.str_255,
            title: validations.str_255,
            image_url: validations.str_255,
            text: validations.str_blob,
            web_text: validations.str_blob,
            video_url: validations.str_255,
            audio_url: validations.str_255,
            question_1: validations.str_255,
            question_2: validations.str_255,
            question_3: validations.str_255,
            question_4: validations.str_255,
            question_5: validations.str_255,
            document_url: validations.str_255,
            document_description: validations.str_255,
            link_description: validations.long_text,
            link_url: validations.str_255,
            question: validations.str_255,
            answer: validations.str_255
          }))
        };

        return Yup.object().shape(schema);
      }),
    onSubmit: async (values) => {
      setIsLoading(true)
      doSetShowLoadingModal(true)

      if (newFlexContent) {
        AddAppResource(uuid, "content/flexes", user.idToken, { data: values }).then((response) =>
          responseProcessorProxy(response)
        );
      } else {
        UpdateContent(uuid, user.idToken, { data: values }).then((response) =>
          responseProcessorProxy(response)
        );
      }
    },
  });

  const loadData = useCallback(() => {
    if (!newFlexContent && !!uuid) {
      setIsLoading(true);

      GetContent(uuid, user.idToken)
        .then((res) => res.json())
        .then((res) => {
          setCurrentDetail({
            id: res?.data?.id,
            title: res?.data?.title,
            description: res?.data?.description,
            cta_type: res?.data?.cta_type || "URL CTA",
            action_bar_text: res?.data?.action_bar_text,
            action_bar_url: res?.data?.action_bar_url,
            enterprise_app: res?.data?.enterprise_app,
            cta: res?.data?.cta,
            image_url: res?.data?.image_url,
            release_date: res?.data?.release_date ?? null,
            sort_order: res?.data?.sort_order,
            tags: res?.data?.tags,
            categories: res?.data?.categories,
            featured: res?.data?.featured,
            sections: res?.data?.sections,
            active: res?.data?.active
          });
          setMetadata(res.data.media_metadata);
          setIsLoading(false);
        })
        .catch((err) => console.error(err));
    }
  }, [newFlexContent, user, uuid])

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contentURL = useMemo(() => {
    let url = ""

    const web = user?.app_data?.setup?.web ?? false;

    if (web) {
      const WEB_FQDN = process.env.REACT_APP_WEB_FQDN;
      const username = user?.app_data?.username;
      const contentType = window.location.pathname.split("/")[1];

      url = `${WEB_FQDN}/${username}/${contentType}/${uuid}`;
    }

    return url
  }, [user?.app_data?.setup?.web, user?.app_data?.username, uuid])

  return (
    <Page links={links} title={`${currentAction} Flex Content`}>
      <EntryForm
        isLoading={isLoading}
        formik={formik}
        submitHandler={formik.handleSubmit}
        cancelURL={`/flex-content`}
      >
        <Column>
          <Input name="title" type="text" label="Title" formik={formik} />
          <TextArea name="description" type="text" label="Description" formik={formik} rows={4} />
          <div className="flex flex-row w-full justify-between pb-4">
          <label
            className={`text-lg mb-5`}
            htmlFor={"cta_type"}
          >
            CTA Type
            <span style={{ color: "red" }}> *</span>
          </label>
          <Radio.Group
            formik={formik}
            className="flex flex-row"
            value={formik?.values?.cta_type ?? "URL CTA"}
            onChange={event => {
              formik.setFieldValue("cta_type", event.target.value)
              formik.setFieldValue("cta", null)
              formik.setFieldValue("action_bar_text", "")
              formik.setFieldValue("action_bar_url", "")
              formik.setFieldValue("enterprise_app", null)
            }}
          >
            <Radio
              value="URL CTA"
            >
              URL CTA
            </Radio>
            <Radio
              value="Preset CTA"
            >
              Preset CTA
            </Radio>
            {
              user?.app_data?.setup.enterprise_launch_business ? (
                <Radio
                  value="Launch Business"
                >
                  Launch Business
                </Radio>
              ) : null
            }
          </Radio.Group>
        </div>

        {
          !["Preset CTA"].includes(formik.values.cta_type) ? (
            <Input
              name="action_bar_text"
              type="text"
              label={`${formik.values.cta_type === "Launch Business" && showBusiness ? "Launch Business" : ""} Call to Action Text`}
              formik={formik}
            />
          ) : null
        }
        {
          formik.values.cta_type === "Launch Business" && showBusiness ? (
            <DataSelector
              name="enterprise_app"
              formik={formik}
              label="Which Business?"
              resourceEndpoint="children"
              filterOptions={(options) => {
                return options.filter((a) => a.active );
              }}
              single  
              singleSelectOnly
              withSearch
            />
          ) : null
        }

        {
          !["Preset CTA", "Launch Business"].includes(formik.values.cta_type) ? (
            <Input
              name="action_bar_url"
              type="text"
              label="Call to Action URL"
              formik={formik}
            />
          ) : null
        }

        {
          formik.values.cta_type === "Preset CTA" ? (
            <DataSelector
              name="cta"
              formik={formik}
              label="Call to Action Name List"
              resourceEndpoint="call_to_actions"
              single
              singleSelectOnly
              withSearch
            />
          ) : null
        }

        { 
          platform_version >= 10.0 && (
            <MediaUploader
              name="image_url"
              type="text"
              label="Image URL"
              formik={formik}
              metadata={metadata}
              mediaType="image"
            />
          )
        }
        </Column>
        <Column>
          <DateSelector
            name="release_date"
            type="text"
            label="Release Date"
            defaultValue={null}
            formik={formik}
            format="DD-MMM-YYYY"
            timezone={user?.data.timezone}
          />
          <Input name="sort_order" type="number" label="Sort Order" formik={formik} />
          <DataSelector
            name="tags"
            formik={formik}
            label="Tags"
            resourceEndpoint="tags?category=Content"
            filterOptions={(values) => values.filter((a) => !a.is_expired)}
          />
          <CheckBox name="active" label="Active" formik={formik} />
          <VersionizerWrapper minVersion={10}>
            <DataSelector
              name="categories"
              formik={formik}
              label="Categories"
              resourceEndpoint="categories"
              sortByName="true"
              filterOptions={(options) => 
                options.filter((status) => status.active)
              }
            />
          </VersionizerWrapper>
          <CheckBox name="featured" label="Featured?" formik={formik} />

          {!!contentURL && (
            <div>
              <div>
                <span>
                  {`Web URL: `}
                  <a
                    href={contentURL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {contentURL}
                  </a>
                </span>
              </div>
            </div>
          )}
        </Column>

        {subTables &&
          Object.keys(subTables).map((key) => {

            return (
              <div className="w-full mb-5 px-5" key={key}>
                <SubTable
                  formik={formik}
                  initialValues={currentDetail}
                  setInitialValues={setCurrentDetail}
                  contentConfig={contentConfig('flexes')}
                  subContentType={key}
                  labels={subTables[key].label}
                  data={{
                    loading: false,
                    count: currentDetail[key] ? currentDetail[key].length : 0,
                    data: currentDetail[key] ? currentDetail[key] : [],
                  }}
                  contentType={'flexes'}
                  onAddClick={() => {}}
                  loading={false}
                />
              </div>
            );
          })}
    </EntryForm>
    </Page>
  )
}

export default FlexContentDetail
